import { useContext, useMemo } from 'react';
import { useQuery } from 'react-query';
import createCachedSelector from 're-reselect';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import * as api from '../../services/api';
import { mappedArray } from '../../utils/array';
import { SORT_BY_STRING } from '../../utils/sorting';
import useCRUD from '../useCRUD';
import useAccount from './useAccount';

const selector = createCachedSelector(
  (data) => data.data,
  (data) => data.storeKey,
  (data) => {
    const labelSorter = SORT_BY_STRING('label');
    const branchCostCenters = data || [];

    const clientBranchCostCentersOptions = branchCostCenters
      .map(({ id, name, identifier }) => ({ value: id, label: `${name} (${identifier})` }))
      .sort(labelSorter);

    const mappedClientBranchCostCenters = mappedArray(branchCostCenters || [], ({ id }) => id);

    return { branchCostCenters, clientBranchCostCentersOptions, mappedClientBranchCostCenters };
  }
)({
  keySelector: (data, storeKey) => storeKey
});

const useClientBranchCostCenters = ({ clientId, clientBranchId } = {}) => {
  const { accountId } = useAccount();
  const { t } = useContext(ConfigContext);

  const storeKey = ['client-branch-cost-centers', accountId, clientId, clientBranchId];

  const { isLoading, data } = useQuery(
    storeKey,
    () => api.fetchClientBranchCostCenters({ accountId, clientId, clientBranchId }),
    {
      staleTime: 0,
      enabled: !!clientId && !!clientBranchId,
      select: (locationsData) => selector(locationsData, storeKey.join('#'))
    }
  );

  const { branchCostCenters, clientBranchCostCentersOptions, mappedClientBranchCostCenters } = useMemo(
    () => ({
      branchCostCenters: data?.branchCostCenters || [],
      clientBranchCostCentersOptions: data?.clientBranchCostCentersOptions || [],
      mappedClientBranchCostCenters: data?.mappedClientBranchCostCenters || {}
    }),
    [data]
  );

  const { addItem, editItem } = useCRUD(
    storeKey,
    {
      addApi: ({ name, identifier }) =>
        api.addClientBranchCostCenters({ accountId, clientId, clientBranchId, name, identifier }),
      editApi: ({ id, name, identifier }) =>
        api.updateClientBranchCostCenters({ accountId, clientId, clientBranchId, costCenterId: id, name, identifier })
    },
    { refetchOnSuccessDelete: true, successToasterMessage: t('changesSaved') }
  );

  return {
    isLoading,
    branchCostCenters,
    clientBranchCostCentersOptions,
    mappedClientBranchCostCenters,
    addClientBranchCostCenter: addItem,
    updateClientBranchCostCenter: editItem
  };
};

export default useClientBranchCostCenters;
